/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { useRef } from 'react';

import MsDyn365, {
    //  eslint-disable-next-line
    IActionContext,
    IComponentProps,
    IGridSettings,
    IImageSettings,
    Image,
    isChannelTypeB2B,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ChannelDeliveryOptionConfiguration, SimpleProduct, Cart } from '@msdyn365-commerce/retail-proxy';
import {
    AttributeValueBase,
    CartLine,
    DiscountLine,
    DiscountLineType,
    ProductCatalog,
    ProductDimensionType,
    SalesLine
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions, getFallbackImageUrl, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { Button, getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IncrementalQuantity as CustomIncrementalQuantity, NomenclatureBundleDetails } from '../../../../modules/component-utilities';
import classNames from 'classnames';
import * as React from 'react';
import EveUserRoles, { UserRolePermissions } from '../../../../modules/eve-user-roles/eve-user-roles';
import PriceVisualization from '../../../../modules/price-visualization/price-visualization';
import { GetAccoutNumberFromContext } from '../../../../utilities/get-account-number-from-context';
import { GetCatalogIdFromContext } from '../../../../utilities/get-catalog-id-from-context';
import { updateAsync, updateCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { PriceComponent, CartLinePriceEditor, ICartLinePriceEditorResources } from '@msdyn365-commerce/components';
import { DatePicker } from 'office-ui-fabric-react';
import { GetStockAvailDate, GetStockAvailDateInput } from '../../../../actions/get-stock-avail-date.action';
import { Getstockavailqty, GetstockavailqtyInput } from '../../../../actions/get-stock-avail-qty.action';
import { GetNomenclatureBundleDetailsJson, GetNomenclatureBundleDetailsInput } from '../../../../actions/get-nomenclature-bundle-details';
import { IDateFormatOptions } from '@msdyn365-commerce/core-internal/dist/types/interfaces/ICultureFormatter';
import { GetUnitTranslation } from '../../../../utilities/get-unit-translation';
import { ParseNomenclatureQuantity } from '../../../../utilities/parse-nomenclature-quantity';

export type CartLineDisplayMode = 'DEFAULT' | 'COMPACT';
/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs.
 */

// export interface ICartlineExtensionProps extends ICartLineProps {
//     dateExpired?: string;

//     cartInstruction?: string;

//     availRequest?: string;
// }

export interface ICartlineResourceString {
    /**
     * Display string for discount label.
     */
    discountStringText: string;

    /**
     * String for size key.
     */
    sizeString: string;

    /**
     * String for color key.
     */
    colorString: string;

    /**
     * String associated with the configuration product dimension.
     */
    configString: string;

    /**
     * String for style key.
     */
    styleString: string;

    /**
     * String for amount key.
     */
    amountString?: string;

    /**
     * Display string for quantity label.
     */
    quantityDisplayString: string;

    /**
     * Display string for quantity label.
     */
    inputQuantityAriaLabel: string;

    /**
     * Aria label for the decrement button in quantity component.
     */
    decrementButtonAriaLabel: string;

    /**
     * Aria label for the increment button in quantity component.
     */
    incrementButtonAriaLabel: string;

    /**
     * Original text screenreader.
     */
    originalPriceText: string;

    /**
     * Current text screenreader.
     */
    currentPriceText: string;

    /**
     * Shipping Charges Text.
     */
    shippingChargesText: string;

    /**
     * Price Editor Charges Text.
     */
    priceEditorResources?: ICartLinePriceEditorResources;

    /**
     * Sales agreement price prompt text.
     */
    salesAgreementPricePrompt?: string;

    /**
     * Shipping text.
     */
    shippingText?: string;

    dateExpired?: string;

    cartInstruction?: string;

    availRequest?: string;

    delivDate?: string;

    cart: Cart | undefined;
}

/**
 * Represents cart line data interface.
 */
interface ICartLineData {
    cartLine: CartLine | SalesLine;
    cartState?: ICartState;
    product?: SimpleProduct;
    catalogs?: ProductCatalog[];
}

/**
 * ICartLineProps: The props required to render cartLineitem.
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /**
     * The flag to change the quantity component from interactible to static.
     */
    disableQuantityInteractivity?: boolean;

    /**
     * The primary image url.
     */
    primaryImageUrl?: string;

    /**
     * The product url.
     */
    productUrl?: string;

    /**
     * GridSettings for the product image in cartLine.
     */
    gridSettings: IGridSettings;

    /**
     * ImageSettings for the product image in cartLine.
     */
    imageSettings: IImageSettings;

    /**
     * Boolean flag to indicate if the item is out of stock.
     */
    isOutOfStock?: boolean;

    /**
     * Flag to make quantity section editable.
     */
    isQuantityEditable?: boolean;

    /**
     * Max quantity for line item.
     */
    maxQuantity?: number;

    /**
     * Current quantity for line item.
     */
    currentQuantity?: number;

    /**
     * Resource string for the component.
     */
    resources: ICartlineResourceString;

    /**
     * SalesLine flag.
     */
    isSalesLine?: boolean;

    /**
     * Error message to show in place of quantity.
     */
    errorMessage?: string;

    /**
     * Display mode to use.
     */
    displayMode?: CartLineDisplayMode;

    /**
     * Inventory information label.
     */
    inventoryInformationLabel?: string;

    /**
     * Inventory information class name.
     */
    inventoryLabelClassName?: string;

    /**
     * Flag to show/hide shipping charges for line items.
     */
    showShippingChargesForLineItems?: boolean;

    /**
     * Boolean flag to indicate if cart state status is ready.
     */
    isCartStateReady?: boolean;

    /**
     * Chanel Delivery Option configuration is from api.
     */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;

    /**
     * The cart line delivery location.
     */
    deliveryLocation?: string;

    /**
     * Quantity onChange callback.
     */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): boolean;

    dateExpired?: string;

    cartInstruction?: string;

    availRequest?: string;
    delivDate?: string;

    cart: Cart | undefined;
    dispositionCode: string | undefined;
}

/**
 * Represents dimensions strings.
 */
interface IDimensionStrings {
    /**
     * String for size key.
     */
    sizeString: string;

    /**
     * String for color key.
     */
    colorString: string;

    /**
     * String associated with the configuration product dimension.
     */
    configString: string;

    /**
     * String for style key.
     */
    styleString: string;

    /**
     * String for amount key.
     */
    amountString?: string;
}

/**
 * Renders catalog label for the cart line.
 * @param props - Cart line props.
 * @returns Catalog label.
 */
const CatalogLabelComponent: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const catalogId = props.data.cartLine.CatalogId;
    validateCatalogId(catalogId);

    if (!isChannelTypeB2B(props.context.actionContext.requestContext) || !catalogId || !ArrayExtensions.hasElements(props.data.catalogs)) {
        return null;
    }

    const catalog = props.data.catalogs.find(item => item.RecordId === catalogId);

    if (!catalog || !catalog.Name) {
        return null;
    }

    return <div className='msc-cart-line__catalog-label'>{catalog.Name}</div>;
};

// Promise
const promiseKey = 'promise-date';
const promiseKeyExpiration = 'promise-date-expiration';
const availQtyKey = 'availQty';
const storedDateKey = 'storedDateKey';
// const [newPromiseDate, setNewPromiseDate] = React.useState<Date | any>();

const getAttributeValue = (cartLine: CartLine, attributeName: string): string => {
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    const attribute: any =
        cartLine.AttributeValues &&
        cartLine.AttributeValues.find((attributeValueBase: AttributeValueBase) => attributeValueBase.Name === attributeName);
    if (attribute && attribute.TextValue) {
        return attribute.TextValue;
    }
    return '';
};

const cartLineItemFunctions = {
    /**
     * Render product dimensions.
     * @param product - Product data.
     * @param dimensionStrings - Dimension strings interface.
     * @returns JSX Element.
     */
    renderProductDimensions: (product: SimpleProduct, dimensionStrings: IDimensionStrings) => {
        if (!product.Dimensions) {
            return [];
        }

        return product.Dimensions.map(productDimension => {
            if (productDimension.DimensionTypeValue === ProductDimensionType.Color) {
                return (
                    <div key={`${product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {dimensionStrings.colorString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Configuration) {
                return (
                    <div key={`${product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-configuration'>
                            {dimensionStrings.configString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Size) {
                return (
                    <div key={`${product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-size'>
                            {dimensionStrings.sizeString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Style) {
                return (
                    <div key={`${product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-style'>
                            {product.IsGiftCard ? dimensionStrings.amountString : dimensionStrings.styleString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }
            return null;
        });
    },

    /**
     * Gets the react node for product unit of price display.
     * @param props - The cart line props.
     * @returns The node representing markup for unit of measure component.
     */
    renderProductUnitPrice: (props: ICartLineProps) => {
        if (!props.data.cartLine.Price) {
            return null;
        }
        const zeroQuantity: number = 0;
        return (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    <PriceComponent
                        data={
                            props.data.cartLine.Quantity && props.data.cartLine.Quantity > zeroQuantity
                                ? {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                    />
                </span>
            </div>
        );
    },

    /**
     * Render discount lines.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderDiscountLines: (props: ICartLineProps) => {
        if (!props.data.cartLine.DiscountLines || !ArrayExtensions.hasElements(props.data.cartLine.DiscountLines)) {
            return null;
        }

        /**
         * Get the updated discount percentage.
         * @param discountLine - The discount line.
         * @param cartLine - The cart line.
         * @returns The updated discount percentage.
         */
        const discountPercent = (discountLine: DiscountLine, cartLine: CartLine): number => {
            const numberZero = 0;
            const decimalPrecision = 2;
            const fullPercentage = 100;
            let updatedPercent: number = discountLine.Percentage ?? numberZero;

            if (
                (discountLine.Percentage === numberZero ||
                    (discountLine.DiscountLineTypeValue && discountLine.DiscountLineTypeValue === DiscountLineType.TenderTypeDiscount)) &&
                cartLine.Price &&
                cartLine.Price > numberZero &&
                cartLine.Quantity &&
                cartLine.Quantity !== numberZero
            ) {
                const effectiveAmount = discountLine.EffectiveAmount ?? numberZero;
                updatedPercent = Number(
                    ((effectiveAmount / (cartLine.Price * cartLine.Quantity)) * fullPercentage).toFixed(decimalPrecision)
                );
            }

            return updatedPercent;
        };

        return props.data.cartLine.DiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId ?? index} className='msc-cart-line__product-discount'>
                    <span>{discountLine.OfferName ? discountLine.OfferName : ''}</span>
                    <span className='msc-cart-line-item-product-discount-price'>
                        <PriceComponent
                            data={
                                props.isSalesLine
                                    ? {
                                          price: {
                                              // @ts-expect-error -- Auto suppressed.
                                              CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                          }
                                      }
                                    : {
                                          price: {
                                              CustomerContextualPrice: discountLine.EffectiveAmount,
                                              BasePrice: discountLine.EffectiveAmount
                                          }
                                      }
                            }
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                        />
                    </span>
                    <span className='msc-cart-line__product-discount-percentage'>
                        {` (${discountLine.Percentage !== undefined ? discountPercent(discountLine, props.data.cartLine) : ''}%)`}
                    </span>
                </div>
            );
        });
    },

    /**
     * Render inventory label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderInventoryLabel: (props: ICartLineProps) => {
        const inventoryCssName = props.inventoryLabelClassName
            ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
            : 'msc-cart-line__product-inventory-label';

        return <p className={inventoryCssName}>{props.inventoryInformationLabel}</p>;
    },

    /**
     * Render shipping label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderShippingLabel: (props: ICartLineProps) => {
        const pickupDeliveryModeCode = props.context.request.channel?.PickupDeliveryModeCode;
        const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
        const cartline = props.data.cartLine;
        let hasShippingMethod = false;
        if (channelDeliveryOptionConfig !== undefined) {
            hasShippingMethod = !!(
                cartline.DeliveryMode &&
                channelDeliveryOptionConfig.PickupDeliveryModeCodes?.some(deliveryMode => deliveryMode !== cartline.DeliveryMode)
            );
        } else {
            hasShippingMethod = !!(cartline.DeliveryMode && cartline.DeliveryMode !== pickupDeliveryModeCode);
        }
        if (!hasShippingMethod) {
            return undefined;
        }

        const shippingChargeLines = (cartline.ChargeLines ?? []).filter(chargeLine => chargeLine.IsShipping);

        if (!ArrayExtensions.hasElements(shippingChargeLines)) {
            return undefined;
        }

        const defaultCharge: number = 0;
        const freightFee = shippingChargeLines.reduce((chargeTotal, chargeLine) => {
            return chargeTotal + (chargeLine.CalculatedAmount ?? defaultCharge);
        }, defaultCharge);

        const priceComponent = (
            <PriceComponent
                data={{
                    price: {
                        CustomerContextualPrice: freightFee
                    }
                }}
                freePriceText='Free'
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__freight-actual'
            />
        );
        return (
            <>
                <label className='msc-cart-line__freight-label'>{`${props.resources.shippingChargesText}:`}</label>
                <span className='shipping-value'>{priceComponent}</span>
            </>
        );
    },

    /**
     * Render other charges.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderOtherCharges: (props: ICartLineProps) => {
        const cartline = props.data.cartLine;
        const otherCharges = cartline.ChargeLines?.filter(chargeline => !chargeline.IsShipping);

        return (
            otherCharges?.map((otherCharge, index) => {
                const itemIndex = index;
                return otherCharge.CalculatedAmount ? (
                    <div className='msc-cart-line__other-charges' key={itemIndex}>
                        <label className='msc-cart-line__other-charges-label'>{`${String(otherCharge.Description)}:`}</label>
                        <span className='other-charge-value'>
                            <PriceComponent
                                data={{
                                    price: {
                                        CustomerContextualPrice: otherCharge.CalculatedAmount
                                    }
                                }}
                                context={props.context}
                                id={props.id}
                                typeName={props.typeName}
                                className='msc-cart-line__other-charges-actual'
                            />
                        </span>
                    </div>
                ) : (
                    ''
                );
            }) ?? undefined
        );
    },
    renderPromiseCartlineAttribute: (
        props: ICartLineProps,
        promiseDate: string,
        storedPromiseDate: string,
        expiration: string,
        availQty: number,
        onAtpRequest: (event: any) => void,
        onDeliveryInput: (e: Date | null | undefined) => void
    ) => {
        const locale = props.context.request.locale;
        var isPromiseExpired = false;
        if (expiration) {
            var expirationDate = new Date(expiration);
            var currentDate = new Date();
            isPromiseExpired = currentDate > expirationDate;
        }
        const isValid = promiseDate && !isPromiseExpired;
        // const cartline = props.data.cartLine;
        const url = props.context.request.canonicalUrl?.split('/');

        const displayDate = new Date(promiseDate);
        displayDate.setHours(0);
        const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

        const delivDate =
            locale === 'fr-CA' ? 'Disponible pour expédition à partir d’un entrepôt MIRAGE ' : 'Available to ship from a MIRAGE warehouse ';
        const shipDateDisclaimer =
            locale === 'fr-CA'
                ? [
                      'Les dates d’expédition varient selon la région.',
                      'Veuillez consulter votre confirmation de commande ou communiquer avec le service à la clientèle pour connaître la date d’expédition exacte.'
                  ]
                : [
                      'Actual ship dates vary by region.',
                      'Please refer to your order confirmation or contact customer service for the actual ship date.'
                  ];
        if (props.context.app.config.enableATP && props.data.cartLine.IsInvoiceLine === false) {
            if (isValid) {
                if (url && url[url?.length - 1].search('checkout') !== -1) {
                    return <></>;
                } else if (
                    url &&
                    url[url?.length - 1].search('order-confirmation') !== -1 &&
                    getAttributeValue(props.data.cartLine, storedDateKey).toString() !== ''
                ) {
                    if (storedPromiseDate) {
                        return (
                            <div className='msc-cart-line_promise-date-container'>
                                <div>
                                    <b>
                                        {delivDate}: {props.context.cultureFormatter.formatDate(new Date(promiseDate), dateOptions)}
                                    </b>
                                    <div className='msc-cart-line__ship-date-disclaimer'>
                                        <span>{shipDateDisclaimer[0]}</span>
                                        <br />
                                        <span>{shipDateDisclaimer[1]}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return <></>;
                } else if (url && url[url?.length - 1].search('orderdetails') !== -1) {
                    return (
                        <div className='msc-cart-line_promise-date-container'>
                            <div>
                                <b>
                                    {delivDate}: {props.context.cultureFormatter.formatDate(new Date(promiseDate), dateOptions)}
                                </b>
                                <div className='msc-cart-line__ship-date-disclaimer'>
                                    <span>{shipDateDisclaimer[0]}</span>
                                    <br />
                                    <span>{shipDateDisclaimer[1]}</span>
                                </div>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className='msc-cart-line_promise-date-container'>
                        <div>
                            <b>{delivDate}: </b>
                        </div>
                        <div>
                            <DatePicker
                                //minDate={new Date(promiseDate)}
                                borderless={true}
                                minDate={availQty <= props.data.cartLine.Quantity! ? new Date(storedPromiseDate) : new Date()}
                                disabled={!isValid}
                                id='deliveryDate'
                                value={displayDate}
                                onSelectDate={onDeliveryInput}
                                formatDate={date => props.context.cultureFormatter.formatDate(date!, dateOptions)}
                                className={'ms-buybox__promise-date-container-input'}
                            />
                        </div>
                    </div>
                );
            } else {
                if (props.displayMode === 'COMPACT') {
                    return (
                        <div className='msc-cart-line__attribute-value msc-cart-line__error-message msc-alert__header'>
                            <b> {props.dateExpired ?? 'Promise date expired!'}</b>
                            <br />
                            {props.cartInstruction ?? 'Please go back to shopping cart and request a new promise date.'}
                        </div>
                    );
                }

                if (
                    (url && url[url?.length - 1].search('checkout') !== -1) ||
                    (url && url[url?.length - 1].search('order-confirmation') !== -1) ||
                    (url && url[url?.length - 1].search('orderdetails') !== -1)
                ) {
                    return <></>;
                }

                return (
                    <div className='ms-buybox__promise-date-container'>
                        <div>
                            <Button className='msc-cart-line__atpButton' onClick={onAtpRequest}>
                                {props.availRequest ?? 'Availability Request'}
                            </Button>
                        </div>
                    </div>
                );
            }
        } else {
            return <></>;
        }
    },

    /**
     * Render the sales agreement prompt.
     * @param props - The ICartLineProps.
     * @returns The JSX.Element.
     */
    renderSalesAgreementPrompt: (props: ICartLineProps): JSX.Element | null => {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- We use 0 to indicate no sales agreement applied on the cart line.
        if (props.data.cartLine.SalesAgreementLineRecordId === 0) {
            return null;
        }
        return <div className='msc-cart-line__sales-agreement-prompt'>{props.resources.salesAgreementPricePrompt}</div>;
    }
};

interface promiseAttr {
    [key: string]: string;
}

async function UpdateCartLinePromise(
    //e: Date | null | undefined,
    actionContext: IActionContext, // | any,
    cart: Cart, // | any,
    currentCartLine: CartLine, // SalesLine | any,
    product: SimpleProduct,
    attributes: promiseAttr[] | any
) {
    if (currentCartLine) {
        const cartLinesObj = cart.CartLines!.filter(cartLine => cartLine.ProductId === product.RecordId);
        for (var attr of attributes) {
            // TODO: add line level attributes
            cartLinesObj.forEach(cartLine => {
                cartLine.AttributeValues = cartLine.AttributeValues!.filter(p => p.Name !== attr.key);
                cartLine.AttributeValues?.push({
                    // @ts-expect-error -- Need to provide data type.
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                    ExtensionProperties: [],
                    Name: attr.key,
                    TextValue: attr.value,
                    TextValueTranslations: []
                });
            });
        }
        const newCart = {
            Id: cart.Id!,
            CartLines: cartLinesObj
        };
        await updateCartLinesAsync({ callerContext: actionContext }, newCart.Id.toString(), newCart.CartLines);
        await updateAsync({ callerContext: actionContext }, newCart!);
    }
}

/**
 * Render cartline component.
 * @param props - The cart line props.
 * @returns JSX element.
 */
// eslint-disable-next-line no-redeclare
const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { isSalesLine, productUrl, resources } = props;
    const { product, cartLine } = props.data;

    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString,
        amountString: resources.amountString
    };

    const fallbackImageUrl = product && getFallbackImageUrl(product.ItemId, props.context.actionContext.requestContext.apiSettings);
    const productDimensions = product && cartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    const renderProductUnitPrice = cartLineItemFunctions.renderProductUnitPrice(props);
    const imageSettings = props.imageSettings;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    const renderDiscountLines = cartLineItemFunctions.renderDiscountLines(props);
    const renderInventoryLabel = cartLineItemFunctions.renderInventoryLabel(props);
    const renderShippingLabel = cartLineItemFunctions.renderShippingLabel(props);
    const renderOtherCharges = cartLineItemFunctions.renderOtherCharges(props);
    const renderSalesAgreementPrompt = cartLineItemFunctions.renderSalesAgreementPrompt(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const productAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const productName = product?.Name ?? cartLine.Description;
    const productNumber = product?.ItemId ?? '';
    const imgeClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-image' : 'msc-cart-line__product-image';
    const cartLineContentClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-content' : 'msc-cart-line__content';
    const [renderPrices, setRenderPrices] = React.useState(false);
    const [promiseDate, setPromiseDate] = React.useState<string>(getAttributeValue(props.data.cartLine, promiseKey));
    const [storedPromiseDate, setStoredPromiseDate] = React.useState<string>('');
    const [availQty, setAvailQty] = React.useState<number>(-1);
    const [hasRoundedUp, setHasRoundedUp] = React.useState<boolean>(false);
    const [nomenclatureBundleDetails, setNomenclatureBundleDetails] = React.useState<any>(null);
    const [isNomenclature, setIsNomenclature] = React.useState<boolean>(false);

    const isFetchingAvailQty = useRef(false);

    React.useEffect(() => {
        let canViewPrices = false;
        if (props.context.app.config.enableUserRoles) {
            const location = props.context.request.url.requestUrl.href.split('/');
            if (location) {
                const cookie = props.context.request.cookies.get<string>(props.context.request.app.config.cookieName);

                let checkout: boolean = false;
                location.forEach(locat => {
                    const parseLoc = locat.split('?');
                    if (parseLoc.find(i => i.toLowerCase() === 'checkout')) {
                        checkout = true;
                    }
                });
                if (checkout) {
                    if (cookie) {
                        canViewPrices = EveUserRoles.UserRolePermissionsHelper(
                            cookie.value ?? '',
                            UserRolePermissions.CanViewProductPriceInCheckout
                        );
                    }
                } else {
                    if (cookie) {
                        canViewPrices = EveUserRoles.UserRolePermissionsHelper(cookie.value ?? '', UserRolePermissions.CanViewProductPrice);
                    }
                }

                if (!checkout && canViewPrices && props.context.app.config.priceExhibition) {
                    const priceVisibilityCookie = props.context.request.cookies.get<string>(PriceVisualization.getCookieName());

                    if (priceVisibilityCookie) {
                        canViewPrices = priceVisibilityCookie.value === 'true' ? true : false;
                    }
                }
            }
        } else {
            canViewPrices = true;
        }

        setRenderPrices(canViewPrices);
    }, []);

    React.useEffect(() => {
        if (cartLine.Quantity && cartLine.Quantity > 0 && product?.ItemId) {
            const input = new GetNomenclatureBundleDetailsInput(product.ItemId, cartLine.Quantity);
            const currentQty = cartLine.Quantity;

            GetNomenclatureBundleDetailsJson(input, props.context.actionContext)
                .then(result => {
                    const totalQuantity = Number(ParseNomenclatureQuantity(result).toFixed(2));

                    if (currentQty > 1 && totalQuantity > 0) {
                        onChange(totalQuantity, true);
                        setNomenclatureBundleDetails(result);
                    } else {
                        setNomenclatureBundleDetails(null);
                    }

                    if (totalQuantity > 0) {
                        setIsNomenclature(true);
                    } else {
                        setIsNomenclature(false);
                    }
                })
                .catch((error: Error) => {
                    props.context.telemetry.error(error.message);
                });
        } else {
            if (nomenclatureBundleDetails) {
                setNomenclatureBundleDetails(null);
                setIsNomenclature(false);
            }
        }
    }, [cartLine.Quantity, product?.ItemId]);

    /**
     * Render other charges.
     * @param newValue - The cart line props.
     * @returns Boolean.
     */
    const onChange = (newValue: number, force: boolean = false): boolean => {
        if (props.quantityOnChange) {
            if (force) {
                setTimeout(() => {
                    props.quantityOnChange!(cartLine as CartLine, newValue);
                }, 0);

                return true;
            }

            const attrPromiseExpiration = getAttributeValue(cartLine, promiseKeyExpiration);
            if (newValue <= availQty) {
                return props.quantityOnChange(cartLine as CartLine, newValue);
            } else {
                if (new Date(promiseDate) < new Date(storedPromiseDate)) {
                    cartLine.AttributeValues = cartLine.AttributeValues!.filter(p => p.Name !== promiseKey);
                    cartLine.AttributeValues?.push({
                        // @ts-expect-error -- Need to provide data type.
                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                        ExtensionProperties: [],
                        Name: promiseKey,
                        TextValue: storedPromiseDate.toString(),
                        TextValueTranslations: []
                    });
                    cartLine.AttributeValues = cartLine.AttributeValues!.filter(p => p.Name !== promiseKeyExpiration);
                    cartLine.AttributeValues?.push({
                        // @ts-expect-error -- Need to provide data type.
                        '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                        ExtensionProperties: [],
                        Name: promiseKeyExpiration,
                        TextValue: attrPromiseExpiration,
                        TextValueTranslations: []
                    });
                    setPromiseDate(storedPromiseDate);
                }
                return props.quantityOnChange(cartLine, newValue);
            }
        }
        return true;
    };

    // Promise
    const onDeliveryInput = (newDeliveryDate: Date | null | undefined) => {
        if (newDeliveryDate) {
            cartLine.RequestedDeliveryDate = newDeliveryDate!;
            var attrPromiseExpiration = getAttributeValue(cartLine, promiseKeyExpiration);
            if (props.data.cartState?.cart) {
                UpdateCartLinePromise(props.context.actionContext, props.data.cartState.cart, cartLine, product!, [
                    {
                        key: promiseKey,
                        value: newDeliveryDate.toString()
                    },
                    {
                        key: promiseKeyExpiration,
                        value: attrPromiseExpiration
                    }
                ]);
            } else if (props.cart) {
                UpdateCartLinePromise(props.context.actionContext, props.cart, cartLine, product!, [
                    {
                        key: promiseKey,
                        value: newDeliveryDate.toString()
                    },
                    {
                        key: promiseKeyExpiration,
                        value: attrPromiseExpiration
                    }
                ]);
            }
            setPromiseDate(newDeliveryDate.toString());
        }
    };

    const onAtpRequest = async (event: { preventDefault: () => void }) => {
        event.preventDefault();
        const catalogId = GetCatalogIdFromContext(props.context.actionContext); // props.context.request.user.catalogId ?? '';
        const accountNumber = GetAccoutNumberFromContext(props.context.actionContext);
        const input: GetStockAvailDateInput = new GetStockAvailDateInput(
            accountNumber,
            catalogId,
            cartLine.ItemId!,
            props.context.app.config.atpDispositionCode,
            cartLine.Quantity!
        );
        const newPromiseDate = await GetStockAvailDate(input, props.context.actionContext);
        var exp = new Date();
        var newHours = exp.getHours() + (isNaN(props.context.app.config.atpTimeout) ? -1 : props.context.app.config.atpTimeout);
        exp.setHours(newHours);
        if (newPromiseDate) {
            onDeliveryInput(null);
            const currentQuantity = cartLine.Quantity ?? 0;
            if (currentQuantity > availQty) {
                UpdateCartLinePromise(props.context.actionContext, props.data.cartState!.cart, cartLine, product!, [
                    {
                        key: promiseKey,
                        value: newPromiseDate.DateAvail.toString()
                    },
                    {
                        key: promiseKeyExpiration,
                        value: exp.toString()
                    },
                    {
                        key: availQtyKey,
                        value: availQty.toString()
                    }
                ]);
                setPromiseDate(newPromiseDate.DateAvail.toString());
            } else {
                const today = new Date();
                UpdateCartLinePromise(props.context.actionContext, props.data.cartState!.cart, cartLine, product!, [
                    {
                        key: promiseKey,
                        value: today.toString()
                    },
                    {
                        key: promiseKeyExpiration,
                        value: exp.toString()
                    },
                    {
                        key: availQtyKey,
                        value: availQty.toString()
                    }
                ]);
                setPromiseDate(today.toString());
            }
            setStoredPromiseDate(newPromiseDate.DateAvail.toString());
        }
    };
    //const promiseAttDate = getAttributeValue(props.data.cartLine, promiseKey);
    const promiseExpiration = getAttributeValue(props.data.cartLine, promiseKeyExpiration);
    const renderPromiseCartlineAttribute = cartLineItemFunctions.renderPromiseCartlineAttribute(
        props,
        promiseDate,
        storedPromiseDate,
        promiseExpiration,
        availQty,
        onAtpRequest,
        onDeliveryInput
    );

    const _generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-cart-line__error-message msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };

    const _generateMultipleMessage = (locale: string | undefined): JSX.Element | null => {
        if (hasRoundedUp) {
            const textToShow =
                locale === 'fr-CA'
                    ? 'La quantité sélectionnée a été arrondi au multiple supérieur le plus près'
                    : 'The quantity selected was round up to the nearest higher multiple';
            return (
                <div className='msc-cart-line__error-message msc-alert__header' style={{ color: '#2c262d' }}>
                    <span className='msi-exclamation-triangle' />
                    <span>{textToShow}</span>
                </div>
            );
        }

        return null;
    };

    /**
     * Render Saving component.
     * @returns JSX element.
     */
    const renderSaving = (): JSX.Element | null => {
        if (!props.data.cartLine.DiscountAmount) {
            return null;
        }
        const discountAmountZero: number = 0;

        if (props.data.cartLine.DiscountAmount > discountAmountZero) {
            return (
                <>
                    <span className='msc-cart-line__product-savings-label'>{`${props.resources.discountStringText || 'Discount'}`}</span>
                    <PriceComponent
                        data={
                            props.isSalesLine
                                ? {
                                      price: {
                                          // @ts-expect-error -- Auto suppressed.
                                          CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.DiscountAmount,
                                          BasePrice: props.data.cartLine.DiscountAmount
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className='msc-cart-line__product-savings-text'
                    />
                </>
            );
        }
        return null;
    };

    /**
     * Gets the react node for product unit of measure display.
     * @returns The node representing markup for unit of measure component.
     */
    const renderUnitOfMeasure = () => {
        if (props.data.cartLine.IsInvoiceLine) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
        if (!props.context.app.config.unitOfMeasureDisplayType || props.context.app.config.unitOfMeasureDisplayType === 'none') {
            return undefined;
        }

        if (!product || !product.DefaultUnitOfMeasure) {
            return undefined;
        }

        return (
            <div className='msc-cart-line__product-unit-of-measure'>
                <span>{GetUnitTranslation(props.context.actionContext, product.DefaultUnitOfMeasure)}</span>
            </div>
        );
    };

    /**
     * Generate quantity and price component.
     * @returns JSX element.
     */
    const generateQuantityAndPrice = (): JSX.Element[] | null => {
        const nodes = [];
        const defaultMaxQuantity: number = 10;
        const singleQuantity: number = 1;
        const unitOfMeasure = renderUnitOfMeasure();

        // No quantity selector for invoice line
        if (!props.data.cartLine.IsInvoiceLine) {
            if (props.data.product && props.isQuantityEditable) {
                nodes.push(
                    <div className='msc-cart-line__product-quantity'>
                        <div className='msc-cart-line__product-quantity-label'>{resources.quantityDisplayString}</div>

                        {_generateErrorMessage()}
                        {_generateMultipleMessage(props.context.actionContext.requestContext.locale)}

                        <CustomIncrementalQuantity
                            id={`msc-cart-line__quantity_${props.data.product.RecordId}-${String(
                                props.data.cartLine.DeliveryMode
                            )}-${String(props.data.cartLine.LineId)}`}
                            key={cartLine.Quantity}
                            max={props.maxQuantity ?? defaultMaxQuantity}
                            currentCount={cartLine.Quantity}
                            onChange={onChange}
                            inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                            decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                            incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                            disabled={!props.isCartStateReady}
                            isGiftCard={props.data.product.IsGiftCard ?? props.isOutOfStock}
                            telemetryContent={props.telemetryContent}
                            locale={props.context.actionContext.requestContext.locale}
                            productMultiple={product?.Behavior?.MultipleOfQuantity}
                            setRoundedUp={setHasRoundedUp}
                            isNomenclature={isNomenclature}
                        />

                        {unitOfMeasure}
                    </div>
                );
            } else {
                nodes.push(
                    <div
                        className={classNames('msc-cart-line__quantity', {
                            'single-quantity': props.data.cartLine.Quantity === singleQuantity
                        })}
                    >
                        {unitOfMeasure}
                        <label className='quantity-label' htmlFor='quantity'>
                            {resources.quantityDisplayString}
                            {props.context.request.locale === 'fr-CA' ? ' : ' : ': '}
                        </label>

                        {_generateErrorMessage()}

                        <span className='quantity-value'>{props.data.cartLine.Quantity}</span>
                    </div>
                );
            }
        }
        if (renderPrices) {
            nodes.push(
                <div className='msc-cart-line__product-savings'>
                    <PriceComponent
                        data={
                            isSalesLine
                                ? {
                                      price: {
                                          CustomerContextualPrice: (props.data.cartLine as SalesLine).NetAmountWithAllInclusiveTax,
                                          BasePrice: (props.data.cartLine as SalesLine).GrossAmount
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.NetAmountWithoutTax,
                                          BasePrice: props.data.cartLine.NetPrice
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className='msc-cart-line__product-savings-actual'
                        originalPriceText={resources.originalPriceText}
                        currentPriceText={resources.currentPriceText}
                    />
                    {renderSaving()}
                    {props.isOutOfStock ? renderInventoryLabel : null}
                </div>
            );
        }
        return nodes;
    };

    const _renderStoreLocation = (): JSX.Element | null => {
        if (cartLine && cartLine.FulfillmentStoreId) {
            return (
                <div className='msc-cart-line__content__bopis-method'>
                    <span className='pick-up'>{props.deliveryLocation}</span>
                </div>
            );
        } else {
            return (
                <div className='msc-cart-line__content__bopis-method'>
                    <span className='shipping'>{props.deliveryLocation}</span>
                </div>
            );
        }
    };

    const callAvailQty = async () => {
        if (isFetchingAvailQty.current) {
            // Prevent duplicate call while one is in progress
            return;
        }

        isFetchingAvailQty.current = true;

        try {
            const catalogId = GetCatalogIdFromContext(props.context.actionContext); // props.context.request.user.catalogId ?? '';
            const accountNumber = GetAccoutNumberFromContext(props.context.actionContext); // props.context.request.user.customerAccountNumber ?? '';
            const itemId = cartLine.ItemId ?? '';
            const displayName = (document.querySelector('.ms-header__profile-button.msc-btn.btn-link') as HTMLElement)?.innerText || '';

            const result = await Getstockavailqty(
                new GetstockavailqtyInput(
                    accountNumber,
                    catalogId,
                    itemId,
                    props.dispositionCode ?? '',
                    props.currentQuantity?.toString() ?? '',
                    displayName
                ),
                props.context.actionContext
            );

            setAvailQty(result.QtyAvail);
        } catch (error) {
            console.error('Failed to fetch Getstockavailqty:', error);
        } finally {
            isFetchingAvailQty.current = false;
        }
    };
    if (availQty === -1) {
        callAvailQty();
    }

    if (storedPromiseDate === '') {
        const catalogId = GetCatalogIdFromContext(props.context.actionContext); // props.context.request.user.catalogId ?? '';
        const accountNumber = GetAccoutNumberFromContext(props.context.actionContext);
        const input: GetStockAvailDateInput = new GetStockAvailDateInput(
            accountNumber,
            catalogId,
            cartLine.ItemId!,
            props.context.app.config.atpDispositionCode,
            cartLine.Quantity!
        );
        GetStockAvailDate(input, props.context.actionContext).then(result => {
            setStoredPromiseDate(result.DateAvail.toString());
        });
    }

    let priceSection = (
        <PriceComponent
            data={
                isSalesLine
                    ? {
                          price: {
                              CustomerContextualPrice: (props.data.cartLine as SalesLine).NetAmountWithAllInclusiveTax,
                              BasePrice: (props.data.cartLine as SalesLine).GrossAmount
                          }
                      }
                    : {
                          price: {
                              CustomerContextualPrice: props.data.cartLine.NetAmountWithoutTax,
                              BasePrice: props.data.cartLine.NetPrice
                          }
                      }
            }
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            className='msc-cart-line__product-discount-value'
        />
    );
    let productPrice = renderProductUnitPrice;
    if (!renderPrices) {
        priceSection = <></>;
        productPrice = <></>;
    }

    const url = props.context.request.canonicalUrl?.split('/');

    return (
        <>
            <div className='msc-cart-line'>
                {MsDyn365.isBrowser ? (
                    <a className={imgeClassName} href={productUrl} aria-label={productName} role='link'>
                        <Image
                            requestContext={props.context.actionContext.requestContext}
                            aria-hidden='true'
                            src={props.primaryImageUrl ?? 'empty'}
                            fallBackSrc={fallbackImageUrl}
                            altText={productName}
                            gridSettings={props.gridSettings}
                            imageSettings={imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </a>
                ) : null}
                <div className={cartLineContentClassName}>
                    <div className='msc-cart-line__product'>
                        <CatalogLabelComponent {...props} />
                        {MsDyn365.isBrowser ? (
                            <a className='msc-cart-line__product-title' href={productUrl} {...productAttribute}>
                                {productName}
                            </a>
                        ) : null}
                        {ArrayExtensions.hasElements(productDimensions) ? (
                            <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                        ) : (
                            ''
                        )}
                        <div className='msc-cart-line__product-price'>
                            {priceSection}
                            {renderUnitOfMeasure()}
                        </div>
                        {MsDyn365.isBrowser && productNumber && url && url[url?.length - 1].search('orderdetails') !== -1 ? (
                            <div className='msc-cart-line__product-sku'>SKU:{productNumber}</div>
                        ) : null}
                        {productPrice}
                        {renderInventoryLabel}
                        {renderDiscountLines}
                        {props.showShippingChargesForLineItems && <div className='msc-cart-line__freight'>{renderShippingLabel}</div>}
                        <div className='msc-cart-line__freight'>{renderShippingLabel}</div>
                        {renderOtherCharges}
                        {renderSalesAgreementPrompt}
                        {nomenclatureBundleDetails && product && props.typeName == 'cart' && (
                            <NomenclatureBundleDetails
                                data={nomenclatureBundleDetails}
                                locale={props.context.actionContext.requestContext.locale}
                                unitOfMeasure={GetUnitTranslation(props.context.actionContext, product.DefaultUnitOfMeasure)}
                                isThinMode={true}
                                canViewPrice={renderPrices}
                                usageContext='cart'
                            />
                        )}
                    </div>
                    {generateQuantityAndPrice()}
                    {renderSalesAgreementPrompt}
                    {props.data.cartLine.IsInvoiceLine && props.data.cartState && props.resources.priceEditorResources && (
                        <CartLinePriceEditor
                            className='msc-cart-line__price-editor-container'
                            context={props.context}
                            resources={props.resources.priceEditorResources}
                            cartState={props.data.cartState}
                            cartLine={props.data.cartLine}
                        />
                    )}
                    {_renderStoreLocation()}
                    {nomenclatureBundleDetails && product && props.typeName !== 'eve-checkout' && props.typeName !== 'cart' && (
                        <NomenclatureBundleDetails
                            data={nomenclatureBundleDetails}
                            locale={props.context.actionContext.requestContext.locale}
                            unitOfMeasure={GetUnitTranslation(props.context.actionContext, product.DefaultUnitOfMeasure)}
                            isThinMode={true}
                            canViewPrice={renderPrices}
                        />
                    )}
                </div>
            </div>
            {renderPromiseCartlineAttribute}
        </>
    );
};

// @ts-expect-error
export const CartLineItemComponent: React.FunctionComponent<ICartLineProps> = msdyn365Commerce.createComponentOverride<ICartline>(
    'CartLineItem',
    {
        component: CartLine,
        ...cartLineItemFunctions
    }
);

export default CartLineItemComponent;
